import React, { useEffect, useMemo, useState } from "react";
import i18n from "utils/i18n";
import { StyleSheet, View } from "react-native";
import Text from 'theme/Text'
import _ from 'lodash';
import Timeslot from 'components/molecules/Timeslot'
import ThemedBlock from 'components/atoms/ThemedBlock'
import EmployeeCard, { EmployeeCardModes } from 'components/molecules/EmployeeCard'
import ThemedList from 'components/atoms/ThemedList'
import Button from 'theme/Button'
import { colorList } from 'utils/colors'
import LoadingComponent from 'components/atoms/Loading'
import FetchBooking from 'utils/fetch/FetchBooking'
import MutateScan from 'utils/mutate/scan'
import { scanThroughConstants } from 'utils/constants/scan-constants'
import * as Animatable from 'react-native-animatable'
import Icon from 'components/atoms/Icon'
import { IconSystemConstants } from 'utils/constants/IconNameConstants'
import HelperString from 'utils/helper-string'
import HelperDate, { dateFormat } from 'utils/helper-date'
import ThemedModal from 'components/atoms/ThemedModal'
import VStack from 'components/atoms/stack/VStack'

const HomeQrCodeValidateBookingModal = ({ visible, setVisible, data }) => {
	
	const [ error, setError ] = useState( null );
	const [ errorTimeLabel, setErrorTimeLabel ] = useState( null );
	const [ isLoading, setIsLoading ] = useState( true );
	const [ isSaving, setIsSaving ] = useState( false );
	const [ employeeBookingDb, setEmployeeBookingDb ] = useState( null );
	
	const employeeBooking = _.get( data, 'employeeBooking' );
	const employee = _.get( data, 'employee' );
	
	const employeeBookingGuid = _.isString( employeeBooking ) ? employeeBooking : _.get( employeeBooking, 'guid' );
	const employeeGuid = _.isString( employee ) ? employee : _.get( employee, 'guid' );
	
	const building = useMemo( () => {
		if( employeeBookingDb ) {
			return employeeBookingDb.building ?? { name: '-' }
		}
		
		return {
			name: '-'
		}
	}, [ employeeBookingDb ] )
	
	const timeslot = useMemo( () => {
		if( employeeBookingDb ) {
			return employeeBookingDb.timeslot ?? {}
		}
		
		return {}
	}, [ employeeBookingDb ] )
	
	const closeModal = () => {
		setVisible( false );
	}
	
	const fetchedBooking = ( bookings ) => {
		const booking = _.get( bookings, [ '0' ], null );
		
		setError( null );
		setIsLoading( false );
		
		if( _.isEmpty( booking ) ) {
			setError( 'code_invalid' );
			return;
		}
		
		// check if booking was today!
		if( HelperDate.date().format( dateFormat.DATE ) !== _.get( booking, 'booking_date' ) ){
			setError( 'code_invalid' );
			return;
		}
		
		// check if we have a scan for the timeslot in the booking already
		_.forEach( _.get( booking, 'employee_scans' ), ( scan ) => {
			if( _.get( scan, [ 'timeslot', 'guid' ]  ) === _.get( booking, [ 'timeslot', 'guid' ] ) ){
				setErrorTimeLabel( _.get( scan, [ 'scanned_in_at' ] ) );
				setError( 'scanned_in' );
			}
		});
		
		setEmployeeBookingDb( booking );
	}
	
	const scanInEmployee = () => {
		const timeslotGuid = _.get( employeeBookingDb, [ 'timeslot', 'guid' ], null );
		MutateScan.scanIn( `building:${_.get( employeeBookingDb, [ 'building', 'guid' ] ) }`, { employeeGuid: _.get( employeeBookingDb, [ 'employee', 'guid' ] ), timeslotGuid: timeslotGuid, scannedInThrough: scanThroughConstants.QRCODE_BY_EMPLOYEE, callbackSuccess: scannedInEmployee, callbackStart: () => {
			setIsSaving(true);
		} })
	}
	
	const scannedInEmployee = () => {
		setTimeout( closeModal, 1000 );
	}
	
	useEffect( () => {
		setError( null );
		setErrorTimeLabel( null );
		
		FetchBooking.fetchByGuid( employeeBookingGuid, employeeGuid, {
			callbackStart: () => {
				setIsLoading( true )
			},
			callbackSuccess: fetchedBooking
		} )
	}, [ data ] );
	
	const timeslotKind = {
		kind: _.get( timeslot, 'kind' ),
		sub_kind: _.get( timeslot, 'sub_kind' )
	};
	
	const finalBooking = useMemo( () => {
		if( employeeBookingDb !== null ) {
			return employeeBookingDb;
		}
		
		return {
			label: _.get( building, 'name', '-' ),
			timeslot: {
				...timeslotKind,
				timeslot_start: _.get( timeslot, 'timeslot_start'),
				timeslot_end: _.get( timeslot, 'timeslot_end')
			}
		};
	}, [ building, timeslotKind, employeeBookingDb ])
	
	const dataBlocks = useMemo( () => {
		if( isLoading || !finalBooking?.employee ) {
			return [];
		}
		
		return [
			<EmployeeCard
				key={ HelperString.hash( 'validate-booking-employee-card' ) }
				employee={ finalBooking?.employee }
				mode={ EmployeeCardModes.listItem }
			/>,
			<Timeslot
				key={ HelperString.hash( 'validate-booking-timeslot' ) }
				item={ finalBooking }
				itemProps={{
					showCapacity: false,
					showLunchOptions: true
				}}
				timeslotKind={ timeslotKind }
				timeslotIconOverWriteProperties={ timeslotKind }
			/>
		];
	}, [ finalBooking, isLoading ] );
	
	return (
		<ThemedModal
			visible={ visible }
			title={ i18n.t('page.validate_booking.title') }>
			
			<VStack>
				{ isLoading && (
					<LoadingComponent size={ 'l' } />
				)}
				
				<ThemedBlock shadow>
					<View style={ [ styles.iconContainer ] }>
						
						{ error === null && (
							<Animatable.View animation={ "jello" } delay={ 800 } >
								<Icon icon={ IconSystemConstants.SUCCESS } style={ [ styles.iconSuccess ] } />
							</Animatable.View>
						)}
						
						{ error !== null && (
							<VStack>
								<Animatable.View animation={ "jello" } delay={ 800 } >
									<Icon icon={ IconSystemConstants.WARNING } style={ [ styles.iconWarning ] } />
								</Animatable.View>
								
								<Text style={ styles.warning }>{ i18n.t( `page.validate_booking.errors.${error}`) }</Text>
								
								{ errorTimeLabel && (
									<Text style={ [ styles.warning, styles.textMarginTop5FontSize11 ] }>{ errorTimeLabel }</Text>
								)}
							</VStack>
						)}
					</View>
				</ThemedBlock>
				
				<ThemedBlock shadow>
					<ThemedList
						style={ styles.themedListPaddingHorizontal0 }
						data={ dataBlocks }
						renderItem={ ( { item } ) => {
							return item;
						} }
					/>
				</ThemedBlock>
				
				{ isSaving && (
					<LoadingComponent size={ 'm' } />
				)}
				
				{ (isLoading === false && isSaving === false && error === null) && (
					<View
						style={styles.viewPaddingHorizontal10}>
						
						<Button
							onPress={ scanInEmployee }>
							{ i18n.t('page.validate_booking.buttons.checkin') }
						</Button>
					</View>
				) }
				
				<Button
					appearance={ 'ghost' }
					onPress={ closeModal }>
					
					{ i18n.t('page.validate_booking.buttons.cancel') }
				</Button>
			</VStack>
		</ThemedModal>
	)
}

export const styles = StyleSheet.create( {
	textMarginTop5FontSize11: {
		marginTop: 5,
		fontSize: 11,
	},
	themedListPaddingHorizontal0: {
		paddingHorizontal: 0,
	},
	viewPaddingHorizontal10: {
		paddingHorizontal: 10,
	},
	warning: {
		marginTop: 20,
		color: colorList.RED.primary,
		textAlign: 'center'
	},
	iconContainer: {
		padding: 20,
		height: 180,
		alignItems: 'center',
		justifyContent: 'center'
	},
	iconSuccess: {
		fontSize: 100,
		color: colorList.GREEN.primary
	},
	iconWarning: {
		fontSize: 100,
		color: colorList.RED.primary
	}
})

export default HomeQrCodeValidateBookingModal
